<script>
    import { debounce } from 'lodash-es';
    import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js/core';
    import metadata from '../../helpers/phone-metadata.json';
    import { _ } from 'svelte-i18n';

    let { firstname, lastname, email, telephone, onChange, onSubmit } = $props();

    // Track which fields have been touched/blurred
    let touched = $state({
        firstname: false,
        lastname: false,
        email: false,
        telephone: false
    });


    /* Validation functions */

    function isValidName(name) {
        return name && name.trim().length >= 2 && /^[a-zA-ZÀ-ÿ\s'-]+$/.test(name.trim());
    }

    function isValidEmail(email) {
        return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    }

    function isValidTelephone(phone) {
        if (!phone || !phone.trim()) return false;
        const trimmed = phone.trim();
        try {
            // Foreign international numbers (e.g., +49...) are accepted without
            // format checking: the bundled metadata only covers NL. Requiring a
            // country code plus at least 10 digits still catches mistakes like
            // +508503710 (should be +31508503710).
            if (trimmed.startsWith('+') && !trimmed.startsWith('+31')) {
                return trimmed.replace(/\D/g, '').length >= 10;
            }
            // Dutch numbers (national format or +31) are validated strictly
            return isValidPhoneNumber(trimmed, 'NL', metadata);
        } catch (error) {
            return false;
        }
    }

    function formatTelephone(phone) {
        if (!phone || !phone.trim()) return phone;

        try {
            // Use national format for Dutch numbers; foreign numbers can't be
            // parsed with the NL-only metadata and are kept as entered
            const phoneNumber = parsePhoneNumber(phone, 'NL', metadata);
            if (phoneNumber && phoneNumber.country === 'NL') {
                return phoneNumber.formatNational();
            }
        } catch (error) {
            // Fall through to return original
        }
        return phone.trim();
    }

    
    /* Handle field input */

    const debouncedOnChange = debounce(() => {
        if (onChange) {
            const validatedValues = {};            
            if (isValidName(firstname)) validatedValues.firstname = firstname.trim();
            if (isValidName(lastname)) validatedValues.lastname = lastname.trim();
            if (isValidEmail(email)) validatedValues.email = email.trim();
            if (isValidTelephone(telephone)) validatedValues.telephone = formatTelephone(telephone);
            
            onChange(validatedValues);
        }
    }, 300);

    $effect(() => {
        // Reference all the reactive props so $effect tracks them
        const currentValues = { firstname, lastname, email, telephone };

        debouncedOnChange();
    });

    function handleSubmit(e) {
        e.preventDefault();
        if (onSubmit) {
            onSubmit();
        }
    }

    function handleBlur(field) {
        touched[field] = true;
    }

    // Derived validation states
    let isFirstnameValid = $derived(!touched.firstname || isValidName(firstname));
    let isLastnameValid = $derived(!touched.lastname || isValidName(lastname));
    let isEmailValid = $derived(!touched.email || isValidEmail(email));
    let isTelephoneValid = $derived(!touched.telephone || isValidTelephone(telephone));

</script>

<form onsubmit={handleSubmit}>
    <div>
        <label>
            <span>{$_('firstname')}</span>
            <input
                type="text"
                autocomplete="given-name"
                bind:value={firstname}
                onblur={() => handleBlur('firstname')}
                aria-invalid={!isFirstnameValid}
                aria-describedby={!isFirstnameValid ? 'error-firstname' : undefined}
            >
            {#if !isFirstnameValid}
                <span class="error" id="error-firstname">{$_('error_name')}</span>
            {/if}
        </label>

        <label style="flex-grow: 3;">
            <span>{$_('lastname')}</span>
            <input
                type="text"
                autocomplete="family-name"
                bind:value={lastname}
                onblur={() => handleBlur('lastname')}
                aria-invalid={!isLastnameValid}
                aria-describedby={!isLastnameValid ? 'error-lastname' : undefined}
            >
            {#if !isLastnameValid}
                <span class="error" id="error-lastname">{$_('error_name')}</span>
            {/if}
        </label>
    </div>

    <div>
        <label>
            <span>{$_('email')}</span>
            <input
                type="email"
                autocomplete="email"
                bind:value={email}
                onblur={() => handleBlur('email')}
                aria-invalid={!isEmailValid}
                aria-describedby={!isEmailValid ? 'error-email' : undefined}
            >
            {#if !isEmailValid}
                <span class="error" id="error-email">{$_('error_email')}</span>
            {/if}
        </label>
    </div>

    <div>
        <label>
            <span>{$_('telephone')}</span>
            <input
                type="tel"
                autocomplete="mobile tel"
                bind:value={telephone}
                onblur={() => handleBlur('telephone')}
                aria-invalid={!isTelephoneValid}
                aria-describedby={!isTelephoneValid ? 'error-telephone' : undefined}
            >
            {#if !isTelephoneValid}
                <span class="error" id="error-telephone">{$_('error_telephone')}</span>
            {/if}
        </label>
    </div>
</form>


<style>

    input[type="text"],
    input[type="email"],
    input[type="tel"] {
        --border-color: color-mix(in srgb, var(--text-color) 10%, var(--background-color));
        --outline-color: color-mix(in srgb, var(--accent-background) 20%, transparent);

        color: var(--text-color);
        background: var(--background-color);

        border: 1px solid var(--border-color);
        border-radius: 5px;

        padding: 0.5em;
        font-size: 16px;
    }
    input[type="text"]::placeholder,
    input[type="email"]::placeholder,
    input[type="tel"]::placeholder {
        color: var(--text-color);
        opacity: 0.2;
    }

    form {
        margin-top: 2em !important;
        margin-bottom: -0.8em !important;
    }
    form div {
        display: flex;
        gap: 1em;
        flex-wrap: wrap;
        margin-bottom: 1em;
    }
    form div label {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }
    form div label span {
        font-size: 0.9em;
        font-weight: 500;
        margin-bottom: 0.5em;
    }

    input:focus {
        --border-color: var(--accent-background);
        --outline-color: color-mix(in srgb, var(--accent-background) 35%, transparent);

        outline: 4px solid var(--outline-color);
        outline-offset: 0;
    }

    .error {
        font-size: 0.85em;
        color: var(--failed-color);
        margin-top: 0.3em;
    }

    input[aria-invalid="true"] {
        --border-color: var(--failed-color);
        --outline-color: color-mix(in srgb, var(--failed-color) 20%, transparent);
    }

    @media (prefers-color-scheme: dark) {
        input[aria-invalid="true"] {
            --outline-color: color-mix(in srgb, var(--failed-color) 50%, transparent);
        }
    }

</style>